/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * "My bookings" — the signed-in renter's reservations, with edit (return date) and cancel.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Car as CarIcon, X, Pencil, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BookingStatus, type RenterBooking } from '../types';
import ModelImage from '../components/ModelImage';
import PaymentMethods from '../components/booking/PaymentMethods';

/** Statuses that are over and done with — no edit/cancel actions. */
const TERMINAL = new Set<number>([
  BookingStatus.Completed,
  BookingStatus.CompletedEarly,
  BookingStatus.Canceled,
  BookingStatus.RequestExpired,
  BookingStatus.RequestDenied,
]);

/** Tailwind classes + label key for each status badge. */
function statusMeta(status: number): { key: string; fallback: string; cls: string } {
  switch (status) {
    case BookingStatus.PendingApproval:
    case BookingStatus.WaitingNewDriverInfo:
      return { key: 'myBookings.status.pending', fallback: 'Pending approval', cls: 'bg-amber-100 text-amber-800' };
    case BookingStatus.ApprovedWaitingForInsuranceApproval:
    case BookingStatus.WaitingForAgreement:
    case BookingStatus.WaitingForCC:
      return { key: 'myBookings.status.action', fallback: 'Action needed', cls: 'bg-blue-100 text-blue-800' };
    case BookingStatus.Approved:
      return { key: 'myBookings.status.approved', fallback: 'Approved', cls: 'bg-green-100 text-green-800' };
    case BookingStatus.Completed:
    case BookingStatus.CompletedEarly:
      return { key: 'myBookings.status.completed', fallback: 'Completed', cls: 'bg-gray-200 text-gray-700' };
    case BookingStatus.Canceled:
      return { key: 'myBookings.status.canceled', fallback: 'Canceled', cls: 'bg-red-100 text-red-700' };
    case BookingStatus.RequestDenied:
      return { key: 'myBookings.status.denied', fallback: 'Denied', cls: 'bg-red-100 text-red-700' };
    case BookingStatus.RequestExpired:
      return { key: 'myBookings.status.expired', fallback: 'Expired', cls: 'bg-gray-200 text-gray-700' };
    case BookingStatus.DepositFailed:
    case BookingStatus.PaymentFailed:
    case BookingStatus.RefundFailed:
      return { key: 'myBookings.status.paymentIssue', fallback: 'Payment issue', cls: 'bg-red-100 text-red-700' };
    default:
      return { key: 'myBookings.status.processing', fallback: 'Processing', cls: 'bg-gray-200 text-gray-700' };
  }
}

export default function MyBookingsPage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [bookings, setBookings] = useState<RenterBooking[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');

  const fmtDate = (s: string) =>
    s ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(s)) : '';

  const load = useCallback(() => {
    setError(null);
    api
      .getRenterBookings()
      .then(setBookings)
      .catch(() => {
        setBookings([]);
        setError(t('myBookings.loadFailed', 'Could not load your bookings.'));
      });
  }, [t]);

  useEffect(() => {
    if (isAuthenticated) load();
    else setBookings([]);
  }, [isAuthenticated, load]);

  const cancel = async (b: RenterBooking) => {
    if (!window.confirm(t('myBookings.confirmCancel', 'Cancel this booking? This cannot be undone.'))) return;
    setBusyId(b.bookingId);
    try {
      const type = await api.checkCancelBooking(b.bookingId).catch(() => 1 /* Simple */);
      const res = await api.cancelBooking(b.bookingId, type);
      if (!res.ok) {
        setError(res.message || t('myBookings.cancelFailed', 'Could not cancel the booking.'));
        return;
      }
      load();
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (b: RenterBooking) => {
    setEditId(b.bookingId);
    setEditDate((b.dateTo || '').slice(0, 10));
    setError(null);
  };

  const saveEdit = async (b: RenterBooking) => {
    if (!editDate) return;
    setBusyId(b.bookingId);
    try {
      // Preserve the original return time; only the date changes.
      const time = (b.dateTo || '').slice(11, 19) || '10:00:00';
      const iso = new Date(`${editDate}T${time}`).toISOString();
      const res = await api.updateBookingDateTo(b.bookingId, iso);
      if (!res.ok) {
        setError(res.message || t('myBookings.updateFailed', 'Could not update the booking.'));
        return;
      }
      setEditId(null);
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (!isAuthenticated)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 text-gray-600">
        <p>{t('myBookings.signInHint', 'Sign in to see your bookings.')}</p>
        <Link to="/" className="mt-3 underline text-blue-600">
          {t('detail.backToSearch', 'Back to search')}
        </Link>
      </div>
    );

  return (
    <div className="min-h-[60vh] bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('myBookings.title', 'My bookings')}</h1>

        <div className="mb-6">
          <PaymentMethods />
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}

        {bookings === null ? (
          <div className="text-gray-500">{t('detail.loading', 'Loading…')}</div>
        ) : bookings.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            <CarIcon className="mx-auto mb-3 opacity-40" size={40} />
            <p>{t('myBookings.empty', 'You have no bookings yet.')}</p>
            <Link to="/" className="mt-3 inline-block underline text-blue-600">
              {t('myBookings.findCar', 'Find a car')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => {
              const meta = statusMeta(b.status);
              const canModify = !TERMINAL.has(b.status);
              const busy = busyId === b.bookingId;
              const title = b.carTitle || `${b.carBrand ?? ''} ${b.carModel ?? ''}`.trim() || t('detail.notFound', 'Car');
              return (
                <div key={b.bookingId} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col sm:flex-row">
                  <Link to={`/car/${b.carId}`} className="sm:w-48 shrink-0">
                    <ModelImage
                      make={b.carBrand}
                      model={b.carModel}
                      fallback={b.carImage}
                      alt={title}
                      className="w-full h-40 sm:h-full object-cover bg-gray-100"
                    />
                  </Link>

                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h2 className="font-semibold text-gray-900">{title}</h2>
                        {b.carYear ? <p className="text-sm text-gray-500">{b.carYear}</p> : null}
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${meta.cls}`}>
                        {t(meta.key, meta.fallback)}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <Calendar size={15} className="opacity-60" />
                      {editId === b.bookingId ? (
                        <span className="flex items-center gap-2 flex-wrap">
                          <span>{fmtDate(b.dateFrom)} →</span>
                          <input
                            type="date"
                            value={editDate}
                            min={(b.dateFrom || '').slice(0, 10)}
                            onChange={(e) => setEditDate(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        </span>
                      ) : (
                        <span>
                          {fmtDate(b.dateFrom)} → {fmtDate(b.dateTo)}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-sm font-medium text-gray-900">${b.price?.toFixed(2)}</div>

                    {canModify && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {editId === b.bookingId ? (
                          <>
                            <button
                              onClick={() => saveEdit(b)}
                              disabled={busy || !editDate}
                              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
                            >
                              <Check size={15} /> {t('myBookings.save', 'Save')}
                            </button>
                            <button
                              onClick={() => setEditId(null)}
                              disabled={busy}
                              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              {t('myBookings.cancelEdit', 'Cancel')}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(b)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                            >
                              <Pencil size={14} /> {t('myBookings.editDates', 'Change return date')}
                            </button>
                            <button
                              onClick={() => cancel(b)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50"
                            >
                              <X size={15} /> {busy ? t('myBookings.working', 'Working…') : t('myBookings.cancel', 'Cancel booking')}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
